import type { ApiClient } from '@carbonwebmail/api-client'
import type {
  CampaignPlanDto,
  EmailBodyDto,
  OrganizationSettingsDto,
  SegmentListDto,
  TemplateListDto,
} from '@carbonwebmail/contracts'
import { designedBody, proseBody } from '@carbonwebmail/domain'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * Writing a campaign, one decision at a time.
 *
 * It creates a draft and nothing else. Sending is a separate press on the list,
 * because the two decisions are genuinely separate: what to say, and whether to
 * say it to several thousand people. A form whose submit button sends is a form
 * somebody submits by pressing Return in a text field.
 *
 * **Five steps rather than one form.** The single form this replaced asked for
 * nine things at once, in no order, with the message at the bottom — so the
 * decision that takes the longest was the one made last and in the least space.
 * Taken in order, each step asks one thing and can say what the answer is for.
 *
 * **One model call, in four places.** The brief typed in the first step is sent
 * once and comes back with a name, an audience and three subject lines, which
 * are then offered across steps one, two and three. Asking three times would
 * cost three times as much, take three times as long, and let the three answers
 * disagree about what the campaign is. The designer is the second and last
 * call, and it is the one somebody asks for by pressing a button.
 *
 * Nothing the model suggests is applied without a press, except the name: a
 * wrong name is overtyped in a second, and a wrong audience is the one mistake
 * a campaign cannot take back.
 *
 * Picking a template **copies** its subject and body into the draft. It is not
 * a link: editing the template later must not change a campaign that has
 * already gone out, and anything that held a reference would invite that.
 */

/** In order. The array below is the order, so the two cannot disagree. */
export const STEPS = ['purpose', 'audience', 'subject', 'message', 'review'] as const

export type WizardStep = (typeof STEPS)[number]

export const STEP_TITLES: Record<WizardStep, string> = {
  purpose: 'What is this campaign for?',
  audience: 'Who gets it?',
  subject: 'The subject line',
  message: 'The message',
  review: 'Review and create',
}

export interface CampaignDraft {
  readonly name: string
  /**
   * What they said the campaign is for, in their own words.
   *
   * Not a campaign field — nothing is stored and nothing is sent. It exists so
   * the first step asks a question somebody can answer off the top of their
   * head, and so there is something for the model to work from.
   */
  readonly brief: string
  /** Empty is the whole subscribed list. It is the default for a reason. */
  readonly segmentId: string
  readonly fromName: string
  readonly fromEmail: string
  /** Empty means replies go to the From address. */
  readonly replyTo: string
  readonly subject: string
  readonly body: EmailBodyDto
}

const EMPTY: CampaignDraft = {
  name: '',
  brief: '',
  segmentId: '',
  fromName: '',
  fromEmail: '',
  replyTo: '',
  subject: '',
  body: proseBody(''),
}

/**
 * What the model came back with, and whether it has been asked yet.
 *
 * `none` is its own state rather than an empty plan: "we asked and it had
 * nothing" and "we have not asked" look identical in a plan object and want
 * opposite things on screen — one offers the button again, the other says it
 * did not work.
 */
export type PlanState =
  | { readonly status: 'idle' }
  | { readonly status: 'thinking' }
  | { readonly status: 'ready'; readonly plan: CampaignPlanDto }
  | { readonly status: 'none' }

export type DesignState =
  | { readonly status: 'idle' }
  | { readonly status: 'designing' }
  | { readonly status: 'done' }
  | { readonly status: 'failed'; readonly why: string }

export function useNewCampaign({
  client,
  fromTemplate,
  onCreated,
}: {
  readonly client: ApiClient
  /** Chosen from a template card before this screen opened. Absent is the ordinary case. */
  readonly fromTemplate: string | undefined
  /**
   * Called once the draft exists, with its id.
   *
   * The id travels because the caller has nowhere useful to send somebody
   * without it: a campaign that has just been written is opened, not looked up
   * again in a list.
   */
  readonly onCreated: (campaignId: string) => void
}) {
  const loadSegments = useCallback(() => client.segments(), [client])
  const { state: segments } = useLoaded<SegmentListDto>(
    loadSegments,
    'We could not load your segments, so the only audience available is your whole list.',
  )

  const loadTemplates = useCallback(() => client.templates(), [client])
  const { state: templates } = useLoaded<TemplateListDto>(
    loadTemplates,
    'We could not load your templates, so you will need to write this one from scratch.',
  )

  const [draft, setDraft] = useState<CampaignDraft>(EMPTY)
  /** Set once the identity has been filled in, so it cannot overwrite typing. */
  const [prefilled, setPrefilled] = useState(false)

  const loadSettings = useCallback(() => client.settings(), [client])
  const { state: settings } = useLoaded<OrganizationSettingsDto>(
    loadSettings,
    'We could not load your sending identity, so the from address starts empty.',
  )

  /**
   * The organisation’s identity is where a campaign starts.
   *
   * Copied in once rather than bound to the settings, because the campaign
   * keeps its own: editing it here must not edit the organisation, and a
   * later change to the organisation must not reach back into this draft.
   */
  useEffect(() => {
    if (settings.status !== 'ready' || prefilled) return
    setPrefilled(true)
    setDraft((current) => ({
      ...current,
      fromName: current.fromName === '' ? settings.value.fromName : current.fromName,
      fromEmail: current.fromEmail === '' ? settings.value.fromEmail : current.fromEmail,
      replyTo: current.replyTo === '' ? settings.value.replyTo : current.replyTo,
    }))
  }, [settings, prefilled])

  const [step, setStep] = useState<WizardStep>('purpose')
  const [plan, setPlan] = useState<PlanState>({ status: 'idle' })
  const [design, setDesign] = useState<DesignState>({ status: 'idle' })
  const [problems, setProblems] = useState<readonly string[]>([])
  const [saving, setSaving] = useState(false)

  const change = useCallback(<K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }))
    // A form stops complaining as soon as somebody starts fixing it. Leaving
    // the reasons up while they type means the screen is criticising what they
    // had rather than what they have.
    setProblems([])
  }, [])

  /**
   * Asks the model to read the brief.
   *
   * The name is filled in; the audience and the subject lines are offered. Both
   * halves of that are deliberate, and the reason is the cost of being wrong:
   * a name nobody outside the organisation reads, against an audience that
   * decides who receives this.
   *
   * Failure is not reported as an error. Every field this touches has a working
   * empty state behind it, so the honest consequence of the model being off or
   * busy is that somebody types a name — which is what they would have done
   * anyway. (`planCampaign` already collapses both server codes to `undefined`.)
   */
  const suggest = useCallback(async () => {
    const brief = draft.brief.trim()
    if (brief === '') return

    setPlan({ status: 'thinking' })
    const suggestion = await client.planCampaign(brief)

    if (suggestion === undefined) {
      setPlan({ status: 'none' })
      return
    }

    setPlan({ status: 'ready', plan: suggestion })
    setDraft((current) =>
      // Never over an name somebody has already written. They asked for a
      // suggestion, not for their own words to be replaced.
      current.name.trim() === '' && suggestion.name !== ''
        ? { ...current, name: suggestion.name }
        : current,
    )
  }, [client, draft.brief])

  /**
   * Asks the designer for a message, from the brief.
   *
   * The same call the Design with AI screen makes, with no conversation id: a
   * campaign being written is a first turn by definition, and keeping the
   * conversation would put a row in that screen's list for something nobody
   * went there to do. Changing the design afterwards happens in the composer,
   * which is where the draft lands.
   */
  const designFromBrief = useCallback(async () => {
    const brief = draft.brief.trim()
    if (brief === '') return

    setDesign({ status: 'designing' })
    const reply = await client.designEmail({
      instruction: `Design this email. ${brief}`,
      history: [],
      current: null,
      references: [],
    })

    if (reply === 'not-configured') {
      setDesign({ status: 'failed', why: 'The designer is not switched on for this account.' })
      return
    }
    if (reply === 'busy') {
      setDesign({ status: 'failed', why: 'The designer is busy. Try that again in a moment.' })
      return
    }
    if ('refused' in reply) {
      setDesign({ status: 'failed', why: reply.refused })
      return
    }
    // A turn that answered with a question rather than a design. Legitimate in
    // the designer, useless here — there is nobody to answer it, because this
    // screen has no conversation to answer it in.
    if (reply.html === null) {
      setDesign({
        status: 'failed',
        why: 'Nothing came back that time. Try saying it differently.',
      })
      return
    }

    // Empty source: there is no builder project behind this yet. Opening it in
    // the composer and saving is what creates one.
    setDraft((current) => ({ ...current, body: designedBody(reply.html ?? '', '') }))
    setDesign({ status: 'done' })
  }, [client, draft.brief])

  /**
   * Copies a template's content in, leaving the name and audience alone.
   *
   * Those two are about *this* campaign and the template says nothing about
   * either — overwriting a name somebody had already typed would be the kind
   * of helpfulness that loses work.
   */
  const applyTemplate = useCallback(
    (templateId: string) => {
      if (templates.status !== 'ready') return
      const chosen = templates.value.templates.find(
        (template) => template.templateId === templateId,
      )
      if (chosen === undefined) return
      setDraft((current) => ({
        ...current,
        // Only when there is not already one. A subject chosen from the model's
        // three, then overwritten by picking a template, is work thrown away.
        subject: current.subject.trim() === '' ? chosen.subject : current.subject,
        body: chosen.body,
      }))
      setDesign({ status: 'idle' })
      setProblems([])
    },
    [templates],
  )

  /**
   * A template chosen before the screen opened, from a card's "Use" button.
   *
   * Applied once, when the templates arrive — the id is in the url and the list
   * is a request, so one has to wait for the other. Guarded on having applied
   * it rather than on the id, so it cannot overwrite what somebody has typed
   * since while the list reloads.
   */
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    if (applied || fromTemplate === undefined || templates.status !== 'ready') return
    setApplied(true)
    applyTemplate(fromTemplate)
  }, [applied, fromTemplate, templates.status, applyTemplate])

  /**
   * Why this step cannot be left yet, or nothing.
   *
   * A sentence rather than a boolean, because a disabled button with no reason
   * beside it is the control people click twice and then give up on. Each one
   * names the field and what is missing from it.
   */
  const blocked = useMemo((): string | undefined => {
    if (step === 'purpose') {
      if (draft.brief.trim() === '') return 'Say what this campaign is for, in a line or two.'
      if (draft.name.trim() === '') return 'Give the campaign a name — only you will see it.'
      return undefined
    }
    // Everyone subscribed is a real audience, so this step always has an answer.
    if (step === 'audience') return undefined
    if (step === 'subject') {
      return draft.subject.trim() === '' ? 'A subject line is what lands in the inbox.' : undefined
    }
    if (step === 'message') {
      const empty =
        draft.body.kind === 'prose' ? draft.body.text.trim() === '' : draft.body.html.trim() === ''
      return empty ? 'There is no message yet.' : undefined
    }
    if (draft.fromName.trim() === '') return 'A from name is who the email appears to be from.'
    if (draft.fromEmail.trim() === '') return 'A from address is required.'
    return undefined
  }, [step, draft])

  const at = STEPS.indexOf(step)

  const next = useCallback(() => {
    // Guarded here as well as on the button: Return in a text field reaches
    // this without going near the button, and a wizard that can be typed past
    // its own validation is a wizard with no validation.
    if (blocked !== undefined) return
    const following = STEPS[at + 1]
    if (following !== undefined) setStep(following)
  }, [at, blocked])

  const back = useCallback(() => {
    const previous = STEPS[at - 1]
    if (previous !== undefined) setStep(previous)
  }, [at])

  /**
   * Jumps to a step from the progress bar.
   *
   * Backwards only. Forwards would land somebody on a question whose answer
   * depends on one they have not given — the subject suggestions come from the
   * brief, and there is no brief yet.
   */
  const goTo = useCallback(
    (wanted: WizardStep) => {
      if (STEPS.indexOf(wanted) < at) setStep(wanted)
    },
    [at],
  )

  const create = useCallback(async () => {
    setSaving(true)
    setProblems([])
    try {
      const outcome = await client.createCampaign({
        name: draft.name,
        // Absent rather than empty: the contract's "everyone" is a missing
        // segment, and an empty string is not a uuid.
        segmentId: draft.segmentId === '' ? null : draft.segmentId,
        fromName: draft.fromName,
        fromEmail: draft.fromEmail,
        replyTo: draft.replyTo,
        subject: draft.subject,
        body: draft.body,
      })

      if (outcome.status === 'created') {
        onCreated(outcome.campaignId)
        return
      }
      setProblems(outcome.problems)
    } catch {
      setProblems(['We could not save that campaign. Please try again in a moment.'])
    } finally {
      setSaving(false)
    }
  }, [client, draft, onCreated])

  return {
    draft,
    segments,
    templates,
    problems,
    saving,
    step,
    at,
    blocked,
    plan,
    design,
    change,
    applyTemplate,
    suggest,
    designFromBrief,
    next,
    back,
    goTo,
    create,
  }
}
